export interface ErmDepartment {
  id: string;
  name: string;
  leadEmail?: string;
  leadName?: string;
}

const DEFAULT_DEPARTMENT_NAMES = [
  'Marketing',
  'Promotions',
  'Sales',
  'Development',
  'Legal',
];

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

const slugify = (value: string): string => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const unique = (items: string[]): string[] => {
  const seen = new Set<string>();
  const next: string[] = [];
  items.forEach((item) => {
    const normalized = String(item || '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    next.push(normalized);
  });
  return next;
};

const buildDefaultDepartmentId = (name: string, index: number): string => {
  const slug = slugify(name);
  if (slug) return `department-${slug}`;
  return `department-${index + 1}`;
};

const normalizeDepartment = (value: unknown, index: number): ErmDepartment | null => {
  const record = toRecord(value);
  const name = String(record.name || '').trim();
  if (!name) return null;
  const fallbackId = buildDefaultDepartmentId(name, index);
  const id = String(record.id || fallbackId).trim() || fallbackId;
  const leadEmail = String(record.leadEmail || '').trim();
  const leadName = String(record.leadName || '').trim();
  return {
    id,
    name,
    leadEmail: leadEmail || undefined,
    leadName: leadName || undefined,
  };
};

export const buildDefaultDepartments = (): ErmDepartment[] => (
  DEFAULT_DEPARTMENT_NAMES.map((name, index) => ({
    id: buildDefaultDepartmentId(name, index),
    name,
  }))
);

export const createDepartmentId = (name: string): string => {
  const slug = slugify(name);
  const random = Math.random().toString(36).slice(2, 8);
  if (slug) return `department-${slug}-${random}`;
  return `department-${Date.now()}`;
};

export const normalizeDepartmentAssignments = (
  value: unknown,
  validDepartmentIds?: Set<string>
): string[] => {
  const source = Array.isArray(value) ? value : [value];
  const normalized = unique(
    source.map((item) => String(item || '').trim()).filter(Boolean)
  );
  if (!validDepartmentIds) return normalized;
  return normalized.filter((departmentId) => validDepartmentIds.has(departmentId));
};

export const parseErmDepartmentPreferences = (preferencesValue: unknown): {
  departments: ErmDepartment[];
  projectDepartmentMap: Record<string, string[]>;
  focusedDepartmentIds: string[];
  hasStoredDepartments: boolean;
} => {
  const preferences = toRecord(preferencesValue);
  const rawDepartments = Array.isArray(preferences.ermDepartments) ? preferences.ermDepartments : [];
  const hasStoredDepartments = rawDepartments.length > 0;
  const normalizedDepartments = rawDepartments
    .map((item, index) => normalizeDepartment(item, index))
    .filter((item): item is ErmDepartment => Boolean(item));
  const departments = normalizedDepartments.length > 0
    ? normalizedDepartments
    : buildDefaultDepartments();
  const validDepartmentIds = new Set(departments.map((department) => department.id));

  const rawProjectMap = toRecord(preferences.ermProjectDepartments);
  const projectDepartmentMap: Record<string, string[]> = {};
  Object.entries(rawProjectMap).forEach(([projectIdRaw, value]) => {
    const projectId = String(projectIdRaw || '').trim();
    if (!projectId) return;
    const departmentIds = normalizeDepartmentAssignments(value, validDepartmentIds);
    if (departmentIds.length === 0) return;
    projectDepartmentMap[projectId] = departmentIds;
  });

  const focusedDepartmentIds = normalizeDepartmentAssignments(
    preferences.ermFocusedDepartmentIds,
    validDepartmentIds
  );

  return {
    departments,
    projectDepartmentMap,
    focusedDepartmentIds,
    hasStoredDepartments,
  };
};

export const removeDepartmentFromProjectMap = (
  projectDepartmentMap: Record<string, string[]>,
  departmentId: string
): Record<string, string[]> => {
  const target = String(departmentId || '').trim();
  if (!target) return { ...projectDepartmentMap };
  const next: Record<string, string[]> = {};
  Object.entries(projectDepartmentMap).forEach(([projectId, ids]) => {
    const remaining = normalizeDepartmentAssignments(ids).filter((id) => id !== target);
    if (remaining.length > 0) next[projectId] = remaining;
  });
  return next;
};
